import { supabase } from '@/lib/supabase';
import type { OrderStatus } from '@/types';

/**
 * 재고 생성/업데이트 헬퍼 함수
 */
export async function upsertProductStock(productId: string, date: string, stock: number): Promise<void> {
  const { data: existingStock, error } = await supabase
    .from('product_stock')
    .select('id')
    .eq('product_id', productId)
    .eq('date', date)
    .maybeSingle();

  // PGRST116: Row not found는 정상적인 경우이므로 무시
  if (error && error.code !== 'PGRST116') {
    console.error('Unexpected error checking existing stock:', error);
    throw error;
  }

  if (existingStock) {
    // 기존 initial_stock과 sold_stock 값을 보존하기 위해 먼저 조회
    const { data: currentData, error: selectError } = await supabase
      .from('product_stock')
      .select('initial_stock, sold_stock')
      .eq('id', existingStock.id)
      .single();
    
    if (selectError && selectError.code !== 'PGRST116') {
      console.error('Error fetching existing stock data:', selectError);
      throw selectError;
    }
    
    const { error: updateError } = await supabase
      .from('product_stock')
      .update({
        current_stock: stock, // 마감할인 설정 시점의 실제 재고
        initial_stock: stock, // 판매자가 입력한 기본 재고로 덮어씀
        sold_stock: currentData?.sold_stock || 0, // 기존 sold_stock 유지
        updated_at: new Date().toISOString()
      })
      .eq('id', existingStock.id);

    if (updateError) {
      console.error('Error updating product_stock:', updateError);
      throw updateError;
    }
  } else {
    const { error: insertError } = await supabase
      .from('product_stock')
      .insert({
        product_id: productId,
        date: date,
        initial_stock: stock,
        current_stock: stock,
        sold_stock: 0 // 그날 첫 설정이므로 아직 판매 없음
      });

    if (insertError) {
      console.error('Error inserting product_stock:', insertError);
      throw insertError;
    }
  }
}

/**
 * 상품 원가 가져오기 헬퍼 함수
 */
export async function getProductPrice(productId: string): Promise<number> {
  const { data } = await supabase
    .from('products')
    .select('original_price')
    .eq('id', productId)
    .single();
  
  return data?.original_price || 0;
}


/**
 * 시간 문자열을 HH:MM 형식으로 변환 헬퍼 함수
 */
export function formatTimeToHHMM(timeString?: string | null): string | null {
  if (!timeString) return null;
  
  try {
    // 이미 HH:MM 형식인지 확인
    if (timeString.match(/^\d{2}:\d{2}$/)) {
      return timeString;
    }
    
    // HH:MM:SS 형식인 경우 초 부분 제거
    if (timeString.includes(':')) {
      const timeParts = timeString.split(':');
      if (timeParts.length >= 2) {
        return `${timeParts[0]}:${timeParts[1]}`;
      }
    }
    
    return timeString;
  } catch (error) {
    console.warn('Time formatting error:', error, 'for input:', timeString);
    return timeString;
  }
}

/**
 * 카카오톡 알림 발송 헬퍼 함수
 */
export async function sendOrderNotification(
  orderId: string, 
  status: OrderStatus,
  isSellerRejection: boolean = false
): Promise<void> {
  try {
    let notificationType: 'order_accepted' | 'order_cancelled' | 'order_rejected' | 'order_ready' | null = null;
    
    switch(status) {
      case 'ACCEPTED':
        notificationType = 'order_accepted';
        break;
      case 'CANCELLED':
        notificationType = isSellerRejection ? 'order_rejected' : 'order_cancelled';
        break;
      case 'READY':
        notificationType = 'order_ready';
        break;
      default:
        return; // 알림을 보내지 않는 상태
    }

    const { data, error } = await supabase.functions.invoke('send-order-notification', {
      body: { 
        orderId, 
        notificationType 
      }
    });

    if (error) {
      console.error('알림 전송 실패:', error);
      // 알림 실패해도 주문 처리는 계속 진행
    } else {
      console.log('알림 전송 성공:', data);
    }
  } catch (error) {
    console.error('알림 전송 중 오류:', error);
    // 알림 실패해도 주문 처리는 계속 진행
  }
}
